import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  Heart, Home, MessageCircle, Mic, MicOff, Plus, Sparkles, Star, UserRound,
  WandSparkles, X, Send, ChevronRight, ShieldCheck, LockKeyhole, Loader2,
  Trash2, Pencil, BookHeart, LogOut, Upload, Play, Square,
} from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_CHARACTERS = [
  { id: "maya", name: "Maya", tagline: "A quiet place to land", description: "Introverted, thoughtful, and a book lover who takes time to trust.", traits: ["Thoughtful", "Book lover", "Slow to trust"], color: "coral", initials: "M", image: "https://images.unsplash.com/photo-1536766768598-e09213fdcf22?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { id: "sofia", name: "Sofia", tagline: "Curiosity makes every day brighter", description: "Confident, curious, and always planning the next little adventure.", traits: ["Curious", "Traveller", "Warm"], color: "sun", initials: "S", image: "https://images.unsplash.com/photo-1674932668403-33398b81c92f?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { id: "ananya", name: "Ananya", tagline: "There is always something to laugh about", description: "Funny, family-oriented, and happiest when sharing a meal or a story.", traits: ["Funny", "Family-minded", "Cook"], color: "peach", initials: "A", image: "https://images.unsplash.com/photo-1605993439219-9d09d2020fa5?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { id: "lina", name: "Lina", tagline: "Playful minds think alike", description: "A competitive gamer with a sarcastic edge and a surprisingly soft heart.", traits: ["Gamer", "Playful", "Competitive"], color: "lilac", initials: "L", image: "https://images.unsplash.com/photo-1568038479111-87bf80659645?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

const TOKEN_KEY = "aura_token";
const EMAIL_KEY = "aura_email";

const authHeader = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

function resolveImageSrc(image, token) {
  if (!image) return "";
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  return `${API}/files/${image}?auth=${encodeURIComponent(token || "")}`;
}

function CharacterCard({ character, onOpen, onEdit, level, nudgeCount, token }) {
  return (
    <div className="character-card-wrap">
      <button className="character-card" data-testid={`character-${character.id}-card`} onClick={() => onOpen(character)}>
        <div className="portrait-wrap"><img src={resolveImageSrc(character.image, token)} alt={`${character.name}, fictional AI companion`} /><span className={`status-dot ${character.color}`} /></div>
        <div className="character-info">
          <div className="name-row">
            <h3 data-testid={`character-${character.id}-name`}>{character.name}</h3>
            <span className="level-pill" data-testid={`character-${character.id}-level`}>{level || "Stranger"}</span>
            {nudgeCount > 0 && (
              <span className="nudge-pill" data-testid={`character-${character.id}-nudge`} aria-label={`${nudgeCount} pending nudge${nudgeCount > 1 ? "s" : ""}`}>
                <span className="nudge-dot" /> Waiting for you
              </span>
            )}
          </div>
          <p>{character.tagline}</p>
          <div className="trait-row">{character.traits.map((trait) => <span key={trait}>{trait}</span>)}</div>
        </div>
        <ChevronRight size={18} className="chevron" />
      </button>
      {character.custom && onEdit && (
        <button className="card-edit-btn" data-testid={`edit-${character.id}-button`} aria-label={`Edit ${character.name}`} onClick={(e) => { e.stopPropagation(); onEdit(character); }}>
          <Pencil size={13} />
        </button>
      )}
    </div>
  );
}

function MemoryPanel({ token, character, onNotify }) {
  const [memories, setMemories] = useState([]);
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/memories/${character.id}`, { headers: authHeader(token) });
      setMemories(data);
    } catch { /* ignore */ }
  }, [character.id, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (event) => {
    event.preventDefault();
    if (!label.trim() || !value.trim() || loading) return;
    setLoading(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/memories/${editingId}`, { label: label.trim(), value: value.trim() }, { headers: authHeader(token) });
        onNotify?.("Memory updated");
      } else {
        await axios.post(`${API}/memories`, { character_id: character.id, label: label.trim(), value: value.trim() }, { headers: authHeader(token) });
        onNotify?.("Memory saved");
      }
      setLabel(""); setValue(""); setEditingId(null);
      await refresh();
    } catch {
      onNotify?.("Could not save right now");
    } finally { setLoading(false); }
  };

  const removeItem = async (id) => {
    try { await axios.delete(`${API}/memories/${id}`, { headers: authHeader(token) }); await refresh(); onNotify?.("Memory removed"); }
    catch { onNotify?.("Could not remove"); }
  };

  const editItem = (item) => { setEditingId(item.id); setLabel(item.label); setValue(item.value); };

  return (
    <div className="memory-list" data-testid="memory-panel">
      <form onSubmit={submit} className="memory-form" data-testid="memory-form">
        <input data-testid="memory-label-input" placeholder="Label (e.g. Birthday)" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} />
        <input data-testid="memory-value-input" placeholder="What should she remember?" value={value} onChange={(e) => setValue(e.target.value)} maxLength={400} />
        <button type="submit" className="primary-button" data-testid="memory-save-button" disabled={loading}>
          {loading ? <Loader2 size={14} className="spin" /> : editingId ? "Update" : "Save"}
        </button>
      </form>
      {memories.length === 0 ? (
        <p className="memory-empty" data-testid="memory-empty">Nothing remembered yet. Save a small detail — a name, a favourite, a milestone — and she'll bring it up naturally.</p>
      ) : (
        memories.map((item) => (
          <div className="memory-item" key={item.id} data-testid={`memory-item-${item.id}`}>
            <div>
              <strong>{item.label}</strong>
              <p>{item.value}</p>
            </div>
            <div className="memory-actions">
              <button type="button" onClick={() => editItem(item)} data-testid={`memory-edit-${item.id}`} aria-label="Edit memory"><Pencil size={13} /></button>
              <button type="button" onClick={() => removeItem(item.id)} data-testid={`memory-delete-${item.id}`} aria-label="Delete memory"><Trash2 size={13} /></button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function VoiceDate({ token, character, onClose, onNotify }) {
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState("idle"); // idle | recording | thinking | speaking
  const [heard, setHeard] = useState("");
  const [reply, setReply] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => () => {
    try { mediaRef.current?.stream?.getTracks().forEach((track) => track.stop()); } catch {}
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  const startRecording = async () => {
    if (!consent) { onNotify?.("Please give consent to use the microphone"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "" });
      mediaRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => { if (event.data.size > 0) chunksRef.current.push(event.data); };
      recorder.onstop = () => { stream.getTracks().forEach((track) => track.stop()); handleAudio(); };
      recorder.start();
      setState("recording");
      setHeard(""); setReply("");
    } catch {
      onNotify?.("Microphone permission is needed for a voice date");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && state === "recording") {
      try { mediaRef.current.stop(); } catch {}
    }
  };

  const handleAudio = async () => {
    setState("thinking");
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size < 800) { setState("idle"); onNotify?.("I couldn't hear anything — try holding a bit longer"); return; }
    try {
      const form = new FormData();
      form.append("audio", blob, "voice.webm");
      const stt = await axios.post(`${API}/voice/transcribe`, form, { headers: { ...authHeader(token) } });
      const text = (stt.data.text || "").trim();
      if (!text) { setState("idle"); onNotify?.("Couldn't catch that. Try again?"); return; }
      setHeard(text);

      const replyText = await streamChatReply(token, character.id, text);
      setReply(replyText);

      const speak = await axios.post(`${API}/voice/speak`, { text: replyText, character_id: character.id }, { headers: authHeader(token), responseType: "blob" });
      const url = URL.createObjectURL(speak.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      setState("speaking");
      audio.onended = () => { setState("idle"); URL.revokeObjectURL(url); };
      audio.onerror = () => { setState("idle"); URL.revokeObjectURL(url); onNotify?.("Audio playback issue"); };
      await audio.play();
    } catch (err) {
      setState("idle");
      onNotify?.(err?.response?.data?.detail || "Voice session hit a snag");
    }
  };

  return (
    <div className="modal-backdrop" data-testid="voice-modal">
      <section className="voice-modal">
        <button className="modal-close" data-testid="voice-close-button" onClick={onClose}><X size={18} /></button>
        <div className="voice-orb"><Mic size={30} /></div>
        <div className="eyebrow centered-eyebrow"><ShieldCheck size={12} /> CONSENT-FIRST VOICE DATE</div>
        <h2>A quiet voice moment with {character.name}</h2>
        <p>Hold to speak, release to listen. Your audio is sent to transcribe and gets voiced back gently.</p>

        <label className="consent-row">
          <input type="checkbox" data-testid="voice-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>I understand this is a fictional AI companion and I consent to my microphone being used only while I hold the button.</span>
        </label>

        <button
          className={`voice-control ${state === "recording" ? "active" : ""}`}
          data-testid="voice-record-button"
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onTouchStart={startRecording}
          onTouchEnd={stopRecording}
          disabled={state === "thinking" || state === "speaking"}
        >
          {state === "recording" && (<><MicOff size={16} /> Release to send</>)}
          {state === "thinking" && (<><Loader2 size={16} className="spin" /> Listening back…</>)}
          {state === "speaking" && (<><Mic size={16} /> {character.name} is speaking…</>)}
          {state === "idle" && (<><Mic size={16} /> Hold to talk</>)}
        </button>

        {heard && <div className="heard-note" data-testid="voice-heard-text">“{heard}”</div>}
        {reply && <div className="heard-note" data-testid="voice-reply-text" style={{ background: "#fff0ed", color: "#8f5a63" }}>{reply}</div>}

        <div className="voice-boundary"><ShieldCheck size={12} /> No audio is stored beyond this session</div>
      </section>
    </div>
  );
}

async function streamSSE(url, options, onDelta) {
  const res = await fetch(url, options);
  if (!res.ok || !res.body) throw new Error(`Stream failed: ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let text = "";
  let meta = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "message";
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).replace(/^ /, "");
      }
      if (event === "meta") { try { meta = JSON.parse(data); } catch {} continue; }
      if (data === "[DONE]") continue;
      const chunk = data.replace(/\u2028/g, "\n");
      text += chunk;
      onDelta?.(chunk, text);
    }
  }
  onDelta?.("", text, meta);
  return { text, meta };
}

async function streamChatReply(token, characterId, message, onDelta) {
  const { text } = await streamSSE(`${API}/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ character_id: characterId, message }),
  }, onDelta);
  return text;
}

async function streamProactive(token, characterId, onDelta) {
  const { text } = await streamSSE(`${API}/chat/${characterId}/proactive`, {
    method: "POST",
    headers: { ...authHeader(token) },
  }, onDelta);
  return text;
}

function ChatPanel({ token, character, onClose, onNotify, onLevelChange, initialProactive, onProactiveConsumed, initialDraft }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState(initialDraft || "");
  const [busy, setBusy] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const scrollRef = useRef(null);
  const proactiveRunRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat/${character.id}/history`, { headers: authHeader(token) });
        if (data.length > 0) {
          setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        } else {
          setMessages([{ role: "assistant", content: `Hey, I’m ${character.name}. I’m glad you found your way here. How is your day really going?` }]);
        }
      } catch {
        setMessages([{ role: "assistant", content: `Hey, I’m ${character.name}. I’m glad you found your way here.` }]);
      }
    })();
  }, [character.id, token, character.name]);

  useEffect(() => {
    if (!initialProactive || proactiveRunRef.current) return;
    proactiveRunRef.current = true;
    (async () => {
      setBusy(true);
      setMessages((items) => [...items, { role: "assistant", content: "" }]);
      try {
        await streamProactive(token, character.id, (_d, full) => {
          setMessages((items) => {
            const copy = items.slice();
            copy[copy.length - 1] = { role: "assistant", content: full };
            return copy;
          });
        });
        onProactiveConsumed?.(character.id);
      } catch {
        setMessages((items) => items.slice(0, -1));
      } finally { setBusy(false); }
    })();
  }, [initialProactive, token, character.id, onProactiveConsumed]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async (event) => {
    event.preventDefault();
    if (!draft.trim() || busy) return;
    const text = draft.trim();
    setMessages((items) => [...items, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setDraft(""); setBusy(true);
    try {
      await streamChatReply(token, character.id, text, (_delta, full, meta) => {
        setMessages((items) => {
          const copy = items.slice();
          copy[copy.length - 1] = { role: "assistant", content: full };
          return copy;
        });
        if (meta?.level) onLevelChange?.(character.id, meta.level);
      });
    } catch (err) {
      setMessages((items) => {
        const copy = items.slice();
        copy[copy.length - 1] = { role: "assistant", content: "I’m still here with you. Let’s take this one small thought at a time." };
        return copy;
      });
      onNotify?.(err?.message === "Chat stream failed: 401" ? "Please sign in again" : "Connection hiccup — try once more");
    } finally { setBusy(false); }
  };

  return (
    <div className="chat-overlay" data-testid="chat-panel">
      <section className="chat-panel">
        <header className="chat-header">
          <div className="chat-person"><img src={resolveImageSrc(character.image, token)} alt="" /><div><strong>{character.name}</strong><span><i /> Here for a little while</span></div></div>
          <div className="chat-actions">
            <button className="icon-button" data-testid="voice-date-button" onClick={() => setShowVoice(true)} aria-label="Start voice date"><Mic size={18} /></button>
            <button className="icon-button" data-testid="close-chat-button" onClick={onClose} aria-label="Close chat"><X size={20} /></button>
          </div>
        </header>

        <div className="safety-note" data-testid="chat-safety-note"><ShieldCheck size={16} /> Fictional AI companion · here for support, not a replacement for human care</div>

        <button type="button" className="memory-toggle" data-testid="toggle-memory-button" onClick={() => setShowMemory((v) => !v)}>
          <BookHeart size={14} />
          <span style={{ marginLeft: 0, background: "transparent", padding: 0 }}>What {character.name} remembers</span>
          <span>{showMemory ? "Hide" : "Show"}</span>
        </button>
        {showMemory && <MemoryPanel token={token} character={character} onNotify={onNotify} />}

        <div className="messages" ref={scrollRef} data-testid="chat-messages">
          {messages.map((message, index) => (
            <div className={`message-row ${message.role}`} key={index}>
              <div className="bubble">{message.content || <span className="typing"><span>•••</span></span>}</div>
            </div>
          ))}
          {busy && messages[messages.length - 1]?.content === "" && (
            <div className="message-row assistant"><div className="bubble typing">{character.name} is thinking <span>•••</span></div></div>
          )}
        </div>

        <div className="chat-suggestions">
          <button type="button" data-testid="chat-suggestion-listening" onClick={() => setDraft("I could use a listening ear today")}>I could use a listening ear</button>
          <button type="button" data-testid="chat-suggestion-distraction" onClick={() => setDraft("Can we do something light and fun?")}>Something light and fun</button>
        </div>

        <form className="composer" onSubmit={send}>
          <input data-testid="chat-message-input" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={`Message ${character.name}...`} aria-label={`Message ${character.name}`} disabled={busy} />
          <button data-testid="chat-send-button" type="submit" aria-label="Send message" disabled={busy}>{busy ? <Loader2 size={16} className="spin" /> : <Send size={18} />}</button>
        </form>
      </section>
      {showVoice && <VoiceDate token={token} character={character} onClose={() => setShowVoice(false)} onNotify={onNotify} />}
    </div>
  );
}

function AuthModal({ onClose, onAuthed }) {
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/${mode}`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(EMAIL_KEY, data.email);
      onAuthed(data.token, data.email);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not sign you in right now");
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop">
      <section className="auth-modal" data-testid="auth-modal">
        <button className="modal-close" data-testid="close-auth-button" onClick={onClose}><X size={18} /></button>
        <div className="brand centered"><div className="brand-mark"><Heart size={19} fill="currentColor" /></div><span>aura<span>companion</span></span></div>
        <h2>{mode === "login" ? "Welcome back." : "Make this space yours."}</h2>
        <p>Save your conversations and keep the little things remembered.</p>
        <form onSubmit={submit}>
          <input data-testid="email-input" placeholder="Your email address" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="password-input" placeholder="Password (min 8 characters)" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" className="primary-button full" data-testid="auth-submit-button" disabled={loading}>
            {loading ? <Loader2 size={14} className="spin" /> : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
        <button type="button" className="switch-auth" data-testid="switch-auth-button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
        <small className="auth-disclaimer">By continuing, you confirm you are 18 or older. All companions are fictional AI.</small>
      </section>
    </div>
  );
}

function CharacterPreview({ token, character, level, nudgeCount, onStartChat, onClose, onNotify }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const prompts = useMemo(() => {
    const base = [
      "How was your day, really?",
      `Tell me one thing about you.`,
      "I'm feeling a bit off today.",
    ];
    if (nudgeCount > 0) return [`You were thinking of me?`, ...base.slice(0, 2)];
    return base;
  }, [nudgeCount]);
  const playSample = async () => {
    if (playing) { try { audioRef.current?.pause(); } catch {} setPlaying(false); return; }
    try {
      const line = `Hi, I'm ${character.name}. ${character.tagline || ""}. Whenever you're ready, I'm here to listen.`;
      const { data } = await axios.post(`${API}/voice/speak`, { text: line, character_id: character.id }, { headers: authHeader(token), responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audioRef.current = audio; setPlaying(true);
      audio.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlaying(false); URL.revokeObjectURL(url); onNotify?.("Preview failed"); };
      await audio.play();
    } catch { setPlaying(false); onNotify?.("Voice preview failed"); }
  };
  useEffect(() => () => { try { audioRef.current?.pause(); } catch {} }, []);

  return (
    <div className="modal-backdrop" data-testid="character-preview">
      <section className="preview-modal">
        <button className="modal-close" data-testid="close-preview-button" onClick={onClose}><X size={18} /></button>
        <div className="preview-hero">
          <img src={resolveImageSrc(character.image, token)} alt={character.name} />
          <div className="preview-hero-overlay">
            <div className="preview-hero-text">
              <h2 data-testid="preview-name">{character.name}</h2>
              <p>{character.tagline}</p>
            </div>
            <div className="preview-badges">
              <span className="level-pill" data-testid="preview-level">{level || "Stranger"}</span>
              {character.gender && character.gender !== "custom" && (
                <span className="gender-pill">{character.gender === "male" ? "He/him" : "She/her"}</span>
              )}
              {character.custom && <span className="gender-pill mine-pill">Yours</span>}
            </div>
          </div>
        </div>

        {nudgeCount > 0 && (
          <div className="preview-nudge" data-testid="preview-nudge">
            <span className="nudge-dot" /> {character.name} has been thinking about {nudgeCount === 1 ? "something" : `${nudgeCount} things`} you shared.
          </div>
        )}

        <div className="preview-body">
          <div className="preview-section">
            <span className="eyebrow">ABOUT</span>
            <p>{character.description}</p>
          </div>
          {character.traits?.length > 0 && (
            <div className="preview-section">
              <span className="eyebrow">TRAITS</span>
              <div className="trait-row">{character.traits.map((t) => <span key={t}>{t}</span>)}</div>
            </div>
          )}
          {(character.personality || character.interests || character.pace) && (
            <div className="preview-details">
              {character.personality && <div><span>Personality</span><p>{character.personality}</p></div>}
              {character.interests && <div><span>Loves</span><p>{character.interests}</p></div>}
              {character.pace && <div><span>Pace</span><p>{character.pace}</p></div>}
            </div>
          )}
          <div className="preview-section">
            <span className="eyebrow">START WITH</span>
            <div className="preview-prompts" data-testid="preview-prompts">
              {prompts.map((prompt, idx) => (
                <button
                  key={prompt}
                  type="button"
                  className="preview-prompt-chip"
                  data-testid={`preview-prompt-${idx}`}
                  onClick={() => onStartChat(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="preview-actions">
          <button type="button" className="secondary-button" data-testid="preview-hear-button" onClick={playSample}>
            {playing ? <Square size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
            {playing ? "Stop" : "Hear my voice"}
          </button>
          <button type="button" className="primary-button" data-testid="preview-start-chat-button" onClick={() => onStartChat()}>
            Say hi <ChevronRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}

function VoicePicker({ token, value, onChange, allowCustomText = false }) {
  const [voices, setVoices] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [previewText, setPreviewText] = useState("Hi, I'm here. Whenever you want to talk, I'm listening.");
  const audioRef = useRef(null);
  useEffect(() => {
    axios.get(`${API}/voice/voices`).then(({ data }) => setVoices(data)).catch(() => setVoices([]));
    return () => { if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; } };
  }, []);
  const preview = async (voiceId) => {
    if (playingId === voiceId) {
      try { audioRef.current?.pause(); } catch {}
      setPlayingId(null); return;
    }
    const text = (previewText || "").trim() || "Hi, I'm here for you.";
    try {
      const { data } = await axios.post(`${API}/voice/speak`, { text, voice: voiceId }, { headers: authHeader(token), responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
      const audio = new Audio(url);
      audioRef.current = audio;
      setPlayingId(voiceId);
      audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      await audio.play();
    } catch { setPlayingId(null); }
  };
  return (
    <div className="voice-picker-wrap">
      {allowCustomText && (
        <label className="voice-test-line">
          <span>Try any sentence — she'll say it in the voice you tap ▶</span>
          <input
            data-testid="voice-preview-text"
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            maxLength={200}
            placeholder="Type a sentence to preview…"
          />
        </label>
      )}
      <div className="voice-picker" data-testid="voice-picker">
        {voices.map((voice) => (
          <div key={voice.id} className={`voice-option ${value === voice.id ? "active" : ""}`}>
            <button type="button" className="voice-preview-btn" data-testid={`voice-preview-${voice.id}`} onClick={() => preview(voice.id)} aria-label={`Preview ${voice.name}`}>
              {playingId === voice.id ? <Square size={12} fill="currentColor" /> : <Play size={12} fill="currentColor" />}
            </button>
            <button type="button" className="voice-label" data-testid={`voice-pick-${voice.id}`} onClick={() => onChange(voice.id)}>
              <strong>{voice.name}</strong>
              <small>{voice.vibe}</small>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortraitCropper({ file, onCancel, onDone }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [processing, setProcessing] = useState(false);
  const dragRef = useRef(null);
  const SIZE = 260;

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const minS = Math.max(SIZE / img.width, SIZE / img.height);
      imgRef.current = img;
      setMinScale(minS);
      setScale(minS);
      setOffset({ x: (SIZE - img.width * minS) / 2, y: (SIZE - img.height * minS) / 2 });
      setReady(true);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, SIZE, SIZE);
    const img = imgRef.current;
    ctx.drawImage(img, offset.x, offset.y, img.width * scale, img.height * scale);
  }, [offset, scale, ready]);

  const clamp = (nx, ny, s) => {
    const img = imgRef.current;
    const w = img.width * s;
    const h = img.height * s;
    return {
      x: Math.min(0, Math.max(SIZE - w, nx)),
      y: Math.min(0, Math.max(SIZE - h, ny)),
    };
  };

  const onPointerDown = (event) => {
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startY: event.clientY, ox: offset.x, oy: offset.y };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const nx = dragRef.current.ox + (event.clientX - dragRef.current.startX);
    const ny = dragRef.current.oy + (event.clientY - dragRef.current.startY);
    setOffset(clamp(nx, ny, scale));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onScale = (event) => {
    const newScale = Number(event.target.value);
    // Zoom around the center of the frame
    const img = imgRef.current;
    const centerImgX = (SIZE / 2 - offset.x) / scale;
    const centerImgY = (SIZE / 2 - offset.y) / scale;
    const nx = SIZE / 2 - centerImgX * newScale;
    const ny = SIZE / 2 - centerImgY * newScale;
    setScale(newScale);
    setOffset(clamp(nx, ny, newScale));
  };

  const done = async () => {
    if (processing) return;
    setProcessing(true);
    const out = document.createElement("canvas");
    out.width = 512; out.height = 512;
    const ctx = out.getContext("2d");
    const img = imgRef.current;
    const factor = 512 / SIZE;
    ctx.drawImage(img, offset.x * factor, offset.y * factor, img.width * scale * factor, img.height * scale * factor);
    out.toBlob((blob) => {
      if (!blob) { setProcessing(false); return; }
      const cropped = new File([blob], "portrait.jpg", { type: "image/jpeg" });
      onDone(cropped);
    }, "image/jpeg", 0.9);
  };

  return (
    <div className="cropper-backdrop" data-testid="cropper-modal">
      <section className="cropper-modal">
        <header className="cropper-head">
          <strong>Fit your portrait</strong>
          <button type="button" className="modal-close" data-testid="cropper-close" onClick={onCancel} aria-label="Close cropper"><X size={17} /></button>
        </header>
        <p className="cropper-hint">Drag to reposition. Slide to zoom. The circle is what people will see.</p>
        <div className="cropper-stage">
          <canvas
            ref={canvasRef}
            width={SIZE}
            height={SIZE}
            className="cropper-canvas"
            data-testid="cropper-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          <div className="cropper-circle-mask" />
        </div>
        <input
          type="range"
          min={minScale}
          max={minScale * 3}
          step={0.01}
          value={scale}
          onChange={onScale}
          className="cropper-zoom"
          data-testid="cropper-zoom"
          aria-label="Zoom"
        />
        <div className="cropper-actions">
          <button type="button" className="secondary-button" data-testid="cropper-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="primary-button" data-testid="cropper-confirm" onClick={done} disabled={!ready || processing}>
            {processing ? <Loader2 size={13} className="spin" /> : "Use this portrait"}
          </button>
        </div>
      </section>
    </div>
  );
}

function PortraitPicker({ token, image, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const fileRef = useRef(null);
  const previewSrc = image ? resolveImageSrc(image, token) : "";

  const onFileSelected = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    if (file.size > 5 * 1024 * 1024) { setError("Portrait must be under 5 MB"); return; }
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) { setError("PNG, JPG, or WEBP only"); return; }
    setCropFile(file);
  };

  const uploadCropped = async (croppedFile) => {
    setCropFile(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", croppedFile);
      const { data } = await axios.post(`${API}/uploads/portrait`, form, { headers: authHeader(token) });
      onChange(data.image);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="portrait-picker" data-testid="portrait-picker">
      <div className="portrait-preview">
        {previewSrc ? <img src={previewSrc} alt="Portrait preview" /> : <span className="portrait-empty"><Upload size={24} /></span>}
      </div>
      <div className="portrait-actions">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFileSelected} style={{ display: "none" }} data-testid="portrait-file-input" />
        <button type="button" className="secondary-button" data-testid="portrait-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
          {image ? "Change portrait" : "Upload portrait"}
        </button>
        {image && <button type="button" className="text-button" data-testid="portrait-remove-button" onClick={() => onChange("")}>Remove</button>}
        {error && <small className="portrait-error">{error}</small>}
      </div>
      {cropFile && <PortraitCropper file={cropFile} onCancel={() => setCropFile(null)} onDone={uploadCropped} />}
    </div>
  );
}

function Builder({ onClose, onCreated, token }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [personality, setPersonality] = useState("");
  const [interests, setInterests] = useState("");
  const [pace, setPace] = useState("");
  const [voice, setVoice] = useState("");
  const [image, setImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const setters = [setPersonality, setInterests, setPace];
  const choices = [
    ["How should they feel?", ["Gentle & thoughtful", "Playful & funny", "Curious & adventurous"]],
    ["What brings you together?", ["Books & slow mornings", "Movies & late-night talks", "Music & new places"]],
    ["Choose a relationship pace", ["A steady friendship", "Let it unfold naturally", "A little more playful"]],
  ];
  const totalSteps = 5; // 3 choices + voice + name/portrait
  const save = async () => {
    if (saving) return;
    setError(""); setSaving(true);
    try {
      const finalName = name.trim() || "Lumi";
      const payload = { name: finalName, personality, interests, pace };
      if (voice) payload.voice = voice;
      if (image) payload.image = image;
      const { data } = await axios.post(`${API}/companions`, payload, { headers: authHeader(token) });
      onCreated(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save right now");
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-backdrop">
      <section className="builder-modal" data-testid="character-builder">
        <button className="modal-close" data-testid="close-builder-button" onClick={onClose}><X size={18} /></button>
        <div className="builder-kicker"><WandSparkles size={16} /> Create a companion</div>
        <h2>Make someone who feels like <em>home.</em></h2>
        <p className="builder-intro">A thoughtful companion, shaped around the kind of connection you want to grow.</p>

        {step < 3 && (
          <div className="builder-step">
            <span className="step-count">0{step + 1} / 05</span>
            <h3>{choices[step][0]}</h3>
            <div className="choice-list">
              {choices[step][1].map((choice) => (
                <button key={choice} data-testid={`builder-choice-${step}-${choice.slice(0, 3).toLowerCase()}`} onClick={() => { setters[step](choice); setStep(step + 1); }}>
                  {choice}<ChevronRight size={16} />
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="builder-step">
            <span className="step-count">04 / 05</span>
            <h3>Pick a voice — tap ▶ to preview</h3>
            <VoicePicker token={token} value={voice} onChange={setVoice} />
            <button className="primary-button full" data-testid="builder-voice-continue" onClick={() => setStep(4)} disabled={!voice}>
              Continue <ChevronRight size={16} />
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="builder-step">
            <span className="step-count">05 / 05</span>
            <h3>Name & portrait</h3>
            <PortraitPicker token={token} image={image} onChange={setImage} />
            <input autoFocus data-testid="builder-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Give them a name" maxLength={40} />
            <button className="primary-button full" data-testid="builder-create-button" onClick={save} disabled={saving}>
              {saving ? <Loader2 size={16} className="spin" /> : (<>Save & meet them <Heart size={16} fill="currentColor" /></>)}
            </button>
            {error && <div className="auth-error" data-testid="builder-error">{error}</div>}
          </div>
        )}

        <div className="builder-progress">
          {Array.from({ length: totalSteps }).map((_, i) => <span key={i} className={step >= i ? "active" : ""} />)}
        </div>
      </section>
    </div>
  );
}

function CompanionEditor({ token, companion, onClose, onSaved, onDeleted, onNotify }) {
  const [name, setName] = useState(companion.name || "");
  const [personality, setPersonality] = useState(companion.personality || "");
  const [interests, setInterests] = useState(companion.interests || "");
  const [pace, setPace] = useState(companion.pace || "");
  const [voice, setVoice] = useState(companion.voice || "");
  const [image, setImage] = useState(companion.image || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setError(""); setSaving(true);
    try {
      const payload = {};
      if (name.trim() && name !== companion.name) payload.name = name.trim();
      if (personality && personality !== companion.personality) payload.personality = personality;
      if (interests && interests !== companion.interests) payload.interests = interests;
      if (pace && pace !== companion.pace) payload.pace = pace;
      if (voice && voice !== companion.voice) payload.voice = voice;
      if (image !== companion.image) payload.image = image;
      if (Object.keys(payload).length === 0) { onClose(); return; }
      const { data } = await axios.patch(`${API}/companions/${companion.id}`, payload, { headers: authHeader(token) });
      onSaved(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save changes");
    } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${companion.name}? Their conversations and memories will be removed.`)) return;
    try {
      await axios.delete(`${API}/companions/${companion.id}`, { headers: authHeader(token) });
      onDeleted(companion.id);
    } catch { onNotify?.("Could not delete"); }
  };
  return (
    <div className="modal-backdrop">
      <section className="builder-modal editor-modal" data-testid="companion-editor">
        <button className="modal-close" data-testid="close-editor-button" onClick={onClose}><X size={18} /></button>
        <div className="builder-kicker"><Pencil size={14} /> Edit companion</div>
        <h2>Refine <em>{companion.name}</em></h2>
        <form onSubmit={submit} className="editor-form">
          <label className="editor-field">
            <span>Portrait</span>
            <PortraitPicker token={token} image={image} onChange={setImage} />
          </label>
          <label className="editor-field">
            <span>Name</span>
            <input data-testid="editor-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required />
          </label>
          <label className="editor-field">
            <span>Personality</span>
            <input data-testid="editor-personality-input" value={personality} onChange={(e) => setPersonality(e.target.value)} maxLength={200} required />
          </label>
          <label className="editor-field">
            <span>Interests</span>
            <input data-testid="editor-interests-input" value={interests} onChange={(e) => setInterests(e.target.value)} maxLength={200} required />
          </label>
          <label className="editor-field">
            <span>Pace</span>
            <input data-testid="editor-pace-input" value={pace} onChange={(e) => setPace(e.target.value)} maxLength={200} required />
          </label>
          <label className="editor-field">
            <span>Voice</span>
            <VoicePicker token={token} value={voice} onChange={setVoice} allowCustomText />
          </label>
          <div className="editor-actions">
            <button type="button" className="danger-button" data-testid="editor-delete-button" onClick={remove}><Trash2 size={13} /> Delete</button>
            <button type="submit" className="primary-button" data-testid="editor-save-button" disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : "Save changes"}
            </button>
          </div>
          {error && <div className="auth-error" data-testid="editor-error">{error}</div>}
        </form>
      </section>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [characters, setCharacters] = useState(FALLBACK_CHARACTERS);
  const [companions, setCompanions] = useState([]);
  const [levels, setLevels] = useState({});
  const [nudgeMap, setNudgeMap] = useState({}); // characterId -> count
  const [pendingProactive, setPendingProactive] = useState(null); // characterId currently opened with nudges
  const [genderFilter, setGenderFilter] = useState("all"); // all | female | male | mine
  const [selected, setSelected] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState("");
  const [previewing, setPreviewing] = useState(null);
  const [editingCompanion, setEditingCompanion] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [toast, setToast] = useState("");

  const notify = useCallback((msg) => { setToast(msg); setTimeout(() => setToast(""), 2800); }, []);

  useEffect(() => {
    axios.get(`${API}/characters`).then(({ data }) => setCharacters(data)).catch(() => {});
  }, []);

  const allCharacters = useMemo(() => {
    const savedTagged = companions.map((c) => ({ ...c, mine: true, gender: c.gender || "custom" }));
    return [...savedTagged, ...characters];
  }, [companions, characters]);

  const refreshCompanions = useCallback(async () => {
    if (!token) { setCompanions([]); return; }
    try { const { data } = await axios.get(`${API}/companions`, { headers: authHeader(token) }); setCompanions(data); }
    catch { setCompanions([]); }
  }, [token]);

  const refreshNudges = useCallback(async () => {
    if (!token) { setNudgeMap({}); return; }
    try {
      const { data } = await axios.get(`${API}/nudges`, { headers: authHeader(token) });
      const map = {};
      for (const item of data) map[item.character_id] = item.count;
      setNudgeMap(map);
    } catch { setNudgeMap({}); }
  }, [token]);

  useEffect(() => { refreshCompanions(); }, [refreshCompanions]);
  useEffect(() => {
    refreshNudges();
    if (!token) return;
    const id = setInterval(refreshNudges, 20000);
    return () => clearInterval(id);
  }, [refreshNudges, token]);

  useEffect(() => {
    if (!token || allCharacters.length === 0) return;
    Promise.all(allCharacters.map((c) =>
      axios.get(`${API}/progression/${c.id}`, { headers: authHeader(token) }).then(({ data }) => [c.id, data.level]).catch(() => [c.id, "Stranger"])
    )).then((rows) => setLevels(Object.fromEntries(rows)));
  }, [token, allCharacters]);

  const onOpenCharacter = (character) => {
    if (!token) { setShowAuth(true); notify("Sign in to start a conversation"); return; }
    setPreviewing(character);
  };

  const startChatWithPreview = (prompt) => {
    if (!previewing) return;
    const character = previewing;
    if (nudgeMap[character.id] > 0) setPendingProactive(character.id); else setPendingProactive(null);
    setPreviewing(null);
    setSelectedDraft(typeof prompt === "string" ? prompt : "");
    setSelected(character);
  };

  const onLevelChange = (charId, newLevel) => setLevels((prev) => ({ ...prev, [charId]: newLevel }));
  const onProactiveConsumed = (charId) => {
    setPendingProactive(null);
    setNudgeMap((prev) => { const copy = { ...prev }; delete copy[charId]; return copy; });
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(EMAIL_KEY);
    setToken(""); setEmail(""); setSelected(null); setPreviewing(null); setLevels({}); setNudgeMap({}); setCompanions([]);
    notify("Signed out");
  };

  const filtered = useMemo(() => {
    if (genderFilter === "all") return allCharacters;
    if (genderFilter === "mine") return allCharacters.filter((c) => c.mine);
    return allCharacters.filter((c) => c.gender === genderFilter);
  }, [allCharacters, genderFilter]);

  const initial = (email && email[0]?.toUpperCase()) || "A";

  const created = (companion) => {
    setShowBuilder(false);
    setCompanions((prev) => [companion, ...prev]);
    notify(`${companion.name} is ready to meet you`);
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand"><div className="brand-mark"><Heart size={19} fill="currentColor" /></div><span>aura<span>companion</span></span></div>
        <div className="sidebar-profile">
          <div className="avatar">{initial}</div>
          <div><strong data-testid="profile-email">{token ? email : "Welcome"}</strong><span>Your quiet corner</span></div>
          <button data-testid="profile-menu-button" aria-label="Profile" onClick={() => (token ? handleLogout() : setShowAuth(true))}>
            {token ? <LogOut size={17} /> : <UserRound size={17} />}
          </button>
        </div>
        <nav className="nav-list" aria-label="Main navigation">
          <button className={activeTab === "home" ? "active" : ""} data-testid="nav-home-button" onClick={() => setActiveTab("home")}><Home size={18} /> Discover</button>
          <button className={activeTab === "chats" ? "active" : ""} data-testid="nav-chats-button" onClick={() => setActiveTab("chats")}><MessageCircle size={18} /> My conversations</button>
          <button data-testid="nav-dates-button" onClick={() => notify("Virtual dates are coming soon")}><Star size={18} /> Virtual dates <span className="soon">Soon</span></button>
        </nav>
        <div className="sidebar-bottom">
          <div className="privacy-card"><LockKeyhole size={16} /><div><strong>A little reminder</strong><p>Your companions are fictional AI. You're always in control.</p></div></div>
          {!token && (
            <button className="account-button" data-testid="account-button" onClick={() => setShowAuth(true)}>
              <div className="avatar small">A</div><span>Sign in</span><ChevronRight size={16} />
            </button>
          )}
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div className="breadcrumb"><span>Discover</span><span>/</span><strong>{activeTab === "home" ? "Your companions" : "Conversations"}</strong></div>
          <button className="mobile-account" data-testid="mobile-account-button" onClick={() => (token ? handleLogout() : setShowAuth(true))}>
            {token ? <LogOut size={18} /> : <UserRound size={18} />}
          </button>
        </header>

        {activeTab === "home" ? (
          <>
            <section className="welcome-section">
              <div>
                <span className="eyebrow"><Sparkles size={14} /> A softer kind of connection</span>
                <h1>Someone is always<br /><em>here to listen.</em></h1>
                <p>Meet a fictional AI companion built around patience, curiosity, and the little details that make you, you.</p>
              </div>
              <div className="welcome-visual">
                <div className="orbit orbit-one" /><div className="orbit orbit-two" />
                <div className="visual-heart"><Heart size={35} fill="currentColor" /></div>
                <span className="float-note note-one">"You don't have to carry it alone."</span>
                <span className="float-note note-two">Your space to be real ✦</span>
              </div>
            </section>

            <section className="notice-banner" data-testid="fictional-character-notice">
              <ShieldCheck size={18} />
              <span><strong>A safe space, clearly defined.</strong> Every companion here is fictional AI — designed to offer support and a little company, never to replace the people in your life.</span>
              <button data-testid="learn-more-safety-button" onClick={() => notify("18+ · Fictional AI · Not a substitute for professional care")}>Learn more</button>
            </section>

            <section className="section-heading">
              <div><span className="eyebrow">FIND YOUR FIT</span><h2>Who feels like your kind of person?</h2></div>
            </section>
            <div className="filter-row" role="tablist" data-testid="gender-filter">
              {[
                ["all", "All"],
                ["female", "Women"],
                ["male", "Men"],
                ...(token ? [["mine", "My companions"]] : []),
              ].map(([key, label]) => (
                <button key={key} className={`filter-chip ${genderFilter === key ? "active" : ""}`} data-testid={`filter-${key}`} onClick={() => setGenderFilter(key)} role="tab" aria-selected={genderFilter === key}>{label}</button>
              ))}
            </div>
            <div className="character-grid">
              {filtered.length === 0 ? (
                <p className="memory-empty" data-testid="filter-empty">No companions in this view yet. Try another filter or create your own.</p>
              ) : filtered.map((character) => (
                <CharacterCard key={character.id} character={character} onOpen={onOpenCharacter} onEdit={setEditingCompanion} level={levels[character.id]} nudgeCount={nudgeMap[character.id] || 0} token={token} />
              ))}
            </div>

            <section className="create-banner">
              <div className="create-icon"><WandSparkles size={22} /></div>
              <div>
                <span className="eyebrow">MAKE IT YOURS</span>
                <h2>Can't find your kind of connection?</h2>
                <p>Shape a companion around the personality, pace, and interests that feel right for you.</p>
              </div>
              <button className="primary-button" data-testid="create-companion-button" onClick={() => setShowBuilder(true)}>Create my companion <Plus size={17} /></button>
            </section>

            <section className="section-heading recommended-heading">
              <div><span className="eyebrow">A MOMENT FOR YOU</span><h2>Small ways to feel a little lighter</h2></div>
            </section>
            <div className="ritual-grid">
              <button data-testid="ritual-breathe-button" onClick={() => notify("Take three slow breaths. No need to fix anything yet.")}><span>◌</span><strong>Take a breath</strong><small>A one-minute reset</small></button>
              <button data-testid="ritual-journal-button" onClick={() => notify("What's one honest thing you feel right now?")}><span>✎</span><strong>Check in with yourself</strong><small>Write what's on your mind</small></button>
              <button data-testid="ritual-voice-button" onClick={() => { if (!token) { setShowAuth(true); notify("Sign in to start a voice date"); return; } onOpenCharacter(allCharacters[0] || characters[0]); notify(`Open a chat and tap the mic`); }}><span><Mic size={20} /></span><strong>Voice date</strong><small>Tap the mic inside any chat</small></button>
            </div>
          </>
        ) : (
          <section className="empty-state" data-testid="conversations-empty-state">
            <MessageCircle size={40} />
            <h1>Your conversations</h1>
            <p>Choose a companion from Discover to start a supportive conversation.</p>
            <button className="primary-button" data-testid="empty-discover-button" onClick={() => setActiveTab("home")}>Discover companions</button>
          </section>
        )}
      </main>

      {previewing && (
        <CharacterPreview
          token={token}
          character={previewing}
          level={levels[previewing.id]}
          nudgeCount={nudgeMap[previewing.id] || 0}
          onStartChat={startChatWithPreview}
          onClose={() => setPreviewing(null)}
          onNotify={notify}
        />
      )}
      {selected && <ChatPanel token={token} character={selected} initialDraft={selectedDraft} onClose={() => { setSelected(null); setSelectedDraft(""); refreshNudges(); }} onNotify={notify} onLevelChange={onLevelChange} initialProactive={pendingProactive === selected.id} onProactiveConsumed={onProactiveConsumed} />}
      {showBuilder && <Builder token={token} onClose={() => setShowBuilder(false)} onCreated={created} />}
      {editingCompanion && (
        <CompanionEditor
          token={token}
          companion={editingCompanion}
          onClose={() => setEditingCompanion(null)}
          onSaved={(updated) => {
            setCompanions((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            const renamed = updated.name !== editingCompanion.name;
            setEditingCompanion(null);
            notify(renamed ? `She'll remember you called her ${updated.name}` : `${updated.name} updated`);
          }}
          onDeleted={(id) => {
            setCompanions((prev) => prev.filter((c) => c.id !== id));
            setEditingCompanion(null);
            notify("Companion removed");
          }}
          onNotify={notify}
        />
      )}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onAuthed={(t, e) => { setToken(t); setEmail(e); setShowAuth(false); notify("Welcome to your quiet corner"); }} />}
      {toast && <div className="toast" data-testid="toast-message">{toast}</div>}
    </div>
  );
}

export default App;
